import { useCallback, useMemo, useRef, useState } from "react";

import { Theme } from "@mui/material/styles";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import CardActions from "@mui/material/CardActions";
import Radio from "@mui/material/Radio";
import CardMedia from "@mui/material/CardMedia";
import CardHeader from "@mui/material/CardHeader";
import Tooltip from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";

import CheckedIcon from "../../icons/CheckedIcon";
import UncheckedIcon from "../../icons/UncheckedIcon";
import { InfoButton } from "../../buttons";

interface Props {
  image?: string;
  selected: boolean;
  title: string;
  tooltip?: string;
  hasError?: boolean;
  disabled?: boolean;
  size?: "small" | "normal";
  onClick?: () => void;
}

// TODO: This component may be unnecessary due to CTD2C-5637
export const SelectCard = ({
  image,
  selected,
  title,
  tooltip,
  hasError,
  disabled,
  size = "normal",
  onClick,
}: Props): JSX.Element => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const iconButtonRef = useRef<HTMLButtonElement>(null);

  const testId = useMemo(
    () => title.split(" ").join("-").toLowerCase(),
    [title]
  );

  const handleCardClick = useCallback(() => {
    if (disabled) {
      return;
    }
    onClick && onClick();
  }, [onClick, disabled]);

  const handleTooltipClose = useCallback(() => setTooltipOpen(false), []);

  const handleHelpIconClick = useCallback(() => {
    setTooltipOpen((prevValue) => !prevValue);
  }, []);

  const getCardStyles = useCallback(
    (theme: Theme) => ({
      display: "flex",
      cursor: "pointer",
      borderWidth: 1,
      "&:hover": {
        boxShadow: theme.shadows[2],
      },
      padding: size === "small" ? theme.spacing(0, 3) : theme.spacing(2.875),
      ...(hasError
        ? {
            borderWidth: 2,
            borderStyle: `solid`,
            borderColor: "error.main",
          }
        : {}),
      ...(selected
        ? {
            borderWidth: 2,
            borderStyle: `solid`,
            borderColor: "primary.main",
          }
        : {}),
      ...(disabled
        ? {
            opacity: 0.6,
            "&, *": {
              cursor: "not-allowed !important",
            },
            "&:hover": {
              boxShadow: "none !important",
            },
          }
        : {}),
    }),
    [hasError, selected, disabled]
  );
  return (
    <Card
      sx={getCardStyles}
      aria-selected={selected}
      data-testid={`${testId}-card`}
      onClick={handleCardClick}
      role="option"
      aria-labelledby={`${testId}-label`}>
      <Box display="flex" flexGrow={1}>
        <CardActions
          sx={(theme) => ({
            marginRight: theme.spacing(2),
            padding: 0,
            height: theme.spacing(8),
            pointerEvents: "none",
          })}>
          <Radio
            checkedIcon={<CheckedIcon />}
            icon={<UncheckedIcon />}
            data-testid={`${testId}-radio-button`}
            checked={selected}
            onClick={onClick}
          />
        </CardActions>
        {image && (
          <CardMedia
            image={image}
            data-testid={`${testId}-image`}
            sx={(theme) => ({
              backgroundSize: "contain",
              width: theme.spacing(7),
              height: theme.spacing(7),
              alignSelf: "center",
              marginRight: theme.spacing(2),
              flexShrink: 0,
              [theme.breakpoints.down("md")]: {
                width: theme.spacing(5),
                height: theme.spacing(5),
              },
            })}
          />
        )}
        <Box display="flex" flexGrow={1} justifyContent="space-between">
          <CardHeader
            title={title}
            titleTypographyProps={{
              "data-testid": `${testId}-title`,
              id: `${testId}-label`,
            }}
          />
          {tooltip && (
            <ClickAwayListener onClickAway={handleTooltipClose}>
              <Tooltip
                onClose={handleTooltipClose}
                open={tooltipOpen}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                title={tooltip ?? ""}>
                <InfoButton
                  ref={iconButtonRef}
                  sx={(theme) => ({
                    height: theme.spacing(3),
                    width: theme.spacing(3),
                    marginTop: theme.spacing(2.5),
                    marginLeft: theme.spacing(1),
                    color: "action.disabledBackground",
                  })}
                  onClick={handleHelpIconClick}
                  data-testid={`${testId}-tooltip`}
                />
              </Tooltip>
            </ClickAwayListener>
          )}
        </Box>
      </Box>
    </Card>
  );
};
